// =============================================================================
// Unit numbers — the numbers written on imported mattresses
// =============================================================================
// Imported mattresses arrive with a number written on them by hand. This page
// records those numbers against the stock already on the rack, so that when a
// van goes out we know exactly which numbered piece the customer received.
//
// Picking WHICH unit goes out happens on the delivery note, not here — the
// person loading the van is the only one who knows which piece they took.
//
// The reconciliation card is expected to show rows in normal operation (someone
// receives stock before numbering it). It's a worklist, not an alarm: stock
// quantity stays the source of truth for what's sellable, and a missing number
// never blocks a sale or a delivery.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPatch } from '../../lib/api';
import { useHasPermission } from '../../lib/permissions';
import { useSubmitGuard } from '../../lib/useSubmitGuard';
import { PageScaffold } from '../../components/PageScaffold';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { StatusPill } from '../../components/StatusPill';
import { useToast } from '../../components/Toast';

type UnitStatus = 'IN_STOCK' | 'DISPATCHED' | 'DELIVERED' | 'RETURNED' | 'WRITTEN_OFF';

const STATUS_TONE: Record<UnitStatus, 'positive' | 'accent' | 'neutral' | 'warning' | 'negative'> = {
  IN_STOCK: 'positive',
  DISPATCHED: 'accent',
  DELIVERED: 'neutral',
  RETURNED: 'warning',
  WRITTEN_OFF: 'negative',
};

const STATUS_LABEL: Record<UnitStatus, string> = {
  IN_STOCK: 'In stock',
  DISPATCHED: 'On the van',
  DELIVERED: 'Delivered',
  RETURNED: 'Returned',
  WRITTEN_OFF: 'Written off',
};

interface StockUnit {
  id: string;
  unitNumber: string;
  status: UnitStatus;
  note: string | null;
  receivedAt: string;
  product: { id: string; sku: string; name: string; size: string | null };
  location: { id: string; name: string };
  deliveryNoteLine: {
    id: string;
    deliveryNote: {
      id: string;
      number: string;
      status: string;
      order: { id: string; orderNo: string } | null;
    };
  } | null;
}

interface ReconRow {
  productId: string;
  sku: string;
  name: string;
  locationId: string;
  locationName: string;
  onHand: number;
  numbered: number;
  drift: number;
}

interface Location {
  id: string;
  name: string;
  kind: string;
}

interface ProductLite {
  id: string;
  sku: string;
  name: string;
  category: { tracksUnitNumbers?: boolean; name: string };
}

export function UnitNumbersPage(): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const canManage = useHasPermission('stock.units.manage');

  const [locationId, setLocationId] = useState('');
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<StockUnit | null>(null);

  const locations = useQuery({ queryKey: ['stock-locations'], queryFn: () => apiGet<Location[]>('/stock/locations') });

  const units = useQuery({
    queryKey: ['stock-units', locationId, status, q],
    queryFn: () =>
      apiGet<StockUnit[]>('/stock/units', {
        ...(locationId ? { locationId } : {}),
        ...(status !== 'all' ? { status } : {}),
        ...(q.trim() ? { q: q.trim() } : {}),
      }),
  });

  const recon = useQuery({
    queryKey: ['stock-units-recon', locationId],
    queryFn: () => apiGet<ReconRow[]>('/stock/units/reconciliation', locationId ? { locationId } : {}),
  });

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['stock-units'] });
    void qc.invalidateQueries({ queryKey: ['stock-units-recon'] });
  };

  const list = units.data ?? [];
  const drift = recon.data ?? [];

  return (
    <PageScaffold
      title="Unit numbers"
      subtitle="The numbers written on imported mattresses — record them here, pick which one ships on the delivery note."
      contentClassName="space-y-4"
      actions={canManage ? <Button onClick={() => setAdding(true)}>Add numbers</Button> : undefined}
    >
      {drift.length > 0 && (
        <Card
          title="Numbers and stock don't match"
          subtitle="Not blocking anything — stock quantity is still what decides availability. This is the list to work through."
          className="border-warning/40"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3">
                  <th className="py-2 pr-3">Product</th>
                  <th className="py-2 px-2">Location</th>
                  <th className="py-2 px-2 text-right">On hand</th>
                  <th className="py-2 px-2 text-right">Numbered</th>
                  <th className="py-2 pl-2">Gap</th>
                </tr>
              </thead>
              <tbody>
                {drift.map((r) => (
                  <tr key={`${r.productId}:${r.locationId}`} className="border-t border-rule-2">
                    <td className="py-2 pr-3">
                      <div className="text-ink">{r.name}</div>
                      <div className="text-xs kc-mono text-ink-3">{r.sku}</div>
                    </td>
                    <td className="py-2 px-2 text-ink-3">{r.locationName}</td>
                    <td className="py-2 px-2 text-right">{r.onHand}</td>
                    <td className="py-2 px-2 text-right">{r.numbered}</td>
                    <td className="py-2 pl-2 text-warning">
                      {r.drift > 0
                        ? `${r.drift} piece${r.drift === 1 ? '' : 's'} with no number`
                        : `${-r.drift} number${r.drift === -1 ? '' : 's'} with no piece`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card bodyClassName="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            className="kc-input w-full sm:w-64"
            placeholder="Search a unit number…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select className="kc-input" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">All locations</option>
            {(locations.data ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
          <select className="kc-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="available">On the rack</option>
            {(Object.keys(STATUS_LABEL) as UnitStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
          <span className="text-sm text-ink-3">{list.length} unit(s)</span>
        </div>
      </Card>

      <Card bodyClassName="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 border-b border-rule-2">
                <th className="px-6 py-2 font-medium">Number</th>
                <th className="px-6 py-2 font-medium">Product</th>
                <th className="px-6 py-2 font-medium">Location</th>
                <th className="px-6 py-2 font-medium">Status</th>
                <th className="px-6 py-2 font-medium">Went out on</th>
                {canManage && <th className="px-6 py-2" />}
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr>
                  <td colSpan={canManage ? 6 : 5} className="px-6 py-8 text-center text-ink-3">
                    {units.isLoading ? 'Loading…' : 'No unit numbers recorded yet.'}
                  </td>
                </tr>
              )}
              {list.map((u) => (
                <tr key={u.id} className="border-b border-rule-2">
                  <td className="px-6 py-3 kc-mono text-ink">{u.unitNumber}</td>
                  <td className="px-6 py-3">
                    <div className="text-ink">{u.product.name}</div>
                    <div className="text-xs kc-mono text-ink-3">{u.product.sku}</div>
                  </td>
                  <td className="px-6 py-3 text-ink-3">{u.location.name}</td>
                  <td className="px-6 py-3">
                    <StatusPill tone={STATUS_TONE[u.status]}>{STATUS_LABEL[u.status]}</StatusPill>
                  </td>
                  <td className="px-6 py-3 text-ink-3">
                    {u.deliveryNoteLine ? (
                      <div>
                        <div className="kc-mono text-xs">{u.deliveryNoteLine.deliveryNote.number}</div>
                        {u.deliveryNoteLine.deliveryNote.order && (
                          <div className="text-xs">{u.deliveryNoteLine.deliveryNote.order.orderNo}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-ink-3">—</span>
                    )}
                  </td>
                  {canManage && (
                    <td className="px-6 py-3 text-right">
                      {u.status !== 'DELIVERED' && u.status !== 'WRITTEN_OFF' && (
                        <button
                          type="button"
                          className="text-xs text-accent hover:underline"
                          onClick={() => setEditing(u)}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {adding && (
        <AddNumbersModal
          locations={locations.data ?? []}
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            refresh();
          }}
        />
      )}
      {editing && (
        <EditUnitModal
          unit={editing}
          locations={locations.data ?? []}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            refresh();
          }}
          toastOk={(m) => toast.show(m, 'success')}
        />
      )}
    </PageScaffold>
  );
}

// ── Add numbers ──────────────────────────────────────────────────────────────
// The operator is copying a short handwritten list off the rack, so the input is
// a textarea (one number per line) rather than five separate fields.
function AddNumbersModal({
  locations,
  onClose,
  onDone,
}: {
  locations: Location[];
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const toast = useToast();
  const guard = useSubmitGuard();
  const [productQ, setProductQ] = useState('');
  const [product, setProduct] = useState<ProductLite | null>(null);
  const [locationId, setLocationId] = useState(locations[0]?.id ?? '');
  const [text, setText] = useState('');

  const search = useQuery({
    queryKey: ['unit-product-search', productQ],
    queryFn: () => apiGet<ProductLite[]>('/products', { q: productQ.trim() }),
    enabled: productQ.trim().length > 0,
  });

  const numbers = useMemo(
    () =>
      text
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean),
    [text],
  );

  const add = useMutation({
    mutationFn: () =>
      apiPost('/stock/units', {
        productId: product!.id,
        locationId,
        unitNumbers: numbers,
      }),
    onSuccess: () => {
      toast.show(`${numbers.length} unit number(s) recorded`, 'success');
      onDone();
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
        'Could not record those numbers.';
      toast.show(msg, 'error');
    },
    onSettled: guard.release,
  });

  const ready = !!product && !!locationId && numbers.length > 0;

  return (
    <Modal title="Record unit numbers" onClose={onClose}>
      <div className="space-y-3">
        {product ? (
          <div className="flex items-center justify-between rounded border border-rule-2 px-3 py-2">
            <div>
              <div className="text-ink">{product.name}</div>
              <div className="text-xs kc-mono text-ink-3">{product.sku}</div>
            </div>
            <button type="button" className="text-xs text-accent hover:underline" onClick={() => setProduct(null)}>
              Change
            </button>
          </div>
        ) : (
          <>
            <Input
              id="unit-product-q"
              label="Product"
              placeholder="Search an imported mattress by SKU or name…"
              value={productQ}
              onChange={(e) => setProductQ(e.target.value)}
            />
            <div className="max-h-48 overflow-y-auto">
              {(search.data ?? []).slice(0, 10).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-paper-2 rounded"
                  onClick={() => setProduct(p)}
                >
                  <div className="text-ink text-sm">{p.name}</div>
                  <div className="text-xs kc-mono text-ink-3">
                    {p.sku} · {p.category.name}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Location</span>
          <select className="kc-input" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
            Unit numbers — one per line
          </span>
          <textarea
            className="kc-input font-mono min-h-32"
            placeholder={'A-1021\nA-1022\nA-1023'}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <span className="text-xs text-ink-3">
            {numbers.length} number(s). Exactly as written on the mattress.
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!ready} loading={add.isPending} onClick={() => guard.run(() => add.mutate())}>
            Record {numbers.length || ''}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Edit / move / write off ──────────────────────────────────────────────────
function EditUnitModal({
  unit,
  locations,
  onClose,
  onDone,
  toastOk,
}: {
  unit: StockUnit;
  locations: Location[];
  onClose: () => void;
  onDone: () => void;
  toastOk: (m: string) => void;
}): JSX.Element {
  const toast = useToast();
  const guard = useSubmitGuard();
  const [unitNumber, setUnitNumber] = useState(unit.unitNumber);
  const [locationId, setLocationId] = useState(unit.location.id);
  const [writeOffReason, setWriteOffReason] = useState('');

  const onError = (e: unknown): void => {
    const msg =
      (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
      'Could not update that unit.';
    toast.show(msg, 'error');
  };

  const save = useMutation({
    mutationFn: () => apiPatch(`/stock/units/${unit.id}`, { unitNumber, locationId }),
    onSuccess: () => {
      toastOk('Unit updated');
      onDone();
    },
    onError,
    onSettled: guard.release,
  });

  const writeOff = useMutation({
    mutationFn: () => apiPost(`/stock/units/${unit.id}/write-off`, { reason: writeOffReason }),
    onSuccess: () => {
      toastOk('Unit written off');
      onDone();
    },
    onError,
    onSettled: guard.release,
  });

  return (
    <Modal title={`Unit ${unit.unitNumber}`} onClose={onClose}>
      <div className="space-y-3">
        <Input
          id="unit-number"
          label="Unit number"
          value={unitNumber}
          onChange={(e) => setUnitNumber(e.target.value)}
        />
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Location</span>
          <select className="kc-input" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>
        <div className="flex justify-end">
          <Button loading={save.isPending} onClick={() => guard.run(() => save.mutate())}>
            Save
          </Button>
        </div>

        <div className="border-t border-rule-2 pt-3">
          <div className="text-xs font-medium text-ink-3 uppercase tracking-wide mb-1">
            Lost or damaged
          </div>
          <p className="text-xs text-ink-3 mb-2">
            Closes the number so it stops appearing in the pickable list. Correct the stock
            quantity separately on the Stock page — that's what posts the write-off to the accounts.
          </p>
          <Input
            id="unit-writeoff"
            placeholder="Reason (required)"
            value={writeOffReason}
            onChange={(e) => setWriteOffReason(e.target.value)}
          />
          <div className="flex justify-end mt-2">
            <Button
              variant="secondary"
              disabled={!writeOffReason.trim()}
              loading={writeOff.isPending}
              onClick={() => guard.run(() => writeOff.mutate())}
            >
              Write off
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
